/** Default inactivity window before SubscribableReconnectingSocket forces reconnect. */
export const SOCKET_NO_ACTIVITY_MS = 35_000;

/** Client-initiated ping interval for JSON `{ type: 'ping' }` / `{ type: 'pong' }` protocols. */
export const SOCKET_PING_INTERVAL_MS = 30_000;

/** Max wait for a pong after a client ping before reconnecting. */
export const SOCKET_PONG_TIMEOUT_MS = 10_000;

/** Quotes V2 uses a shorter server-driven keepalive cadence. */
export const QUOTES_V2_NO_ACTIVITY_MS = 25_000;

export interface SocketHeartbeatConfig {
  /** When false, ping timer no-ops (e.g. socket not running). Defaults to always true. */
  isActive?: () => boolean;
  onMissedHeartbeat: () => void;
  onPing: () => void;
  pingIntervalMs?: number;
  pongTimeoutMs?: number;
}

/**
 * Client-initiated application ping with pong timeout.
 * Use when the backend expects `{ type: 'ping' }` and replies with `{ type: 'pong' }`.
 */
export class SocketHeartbeat {
  private readonly config: Required<Pick<SocketHeartbeatConfig, 'onMissedHeartbeat' | 'onPing'>> &
    Pick<SocketHeartbeatConfig, 'isActive'> & {
      pingIntervalMs: number;
      pongTimeoutMs: number;
    };

  private pingInterval?: ReturnType<typeof setInterval>;
  private pongTimeout?: ReturnType<typeof setTimeout>;
  private awaitingPong = false;

  constructor(config: SocketHeartbeatConfig) {
    this.config = {
      isActive: config.isActive,
      onMissedHeartbeat: config.onMissedHeartbeat,
      onPing: config.onPing,
      pingIntervalMs: config.pingIntervalMs ?? SOCKET_PING_INTERVAL_MS,
      pongTimeoutMs: config.pongTimeoutMs ?? SOCKET_PONG_TIMEOUT_MS,
    };
  }

  public start = (): void => {
    this.stop();
    this.pingInterval = setInterval(this.sendPing, this.config.pingIntervalMs);
  };

  public stop = (): void => {
    clearInterval(this.pingInterval);
    this.pingInterval = undefined;
    this.clearPongTimeout();
    this.awaitingPong = false;
  };

  public notifyPongReceived = (): void => {
    this.clearPongTimeout();
    this.awaitingPong = false;
  };

  private sendPing = (): void => {
    if (this.config.isActive && !this.config.isActive()) {
      return;
    }
    if (this.awaitingPong) {
      this.handlePongTimeout();
      return;
    }
    this.config.onPing();
    this.awaitingPong = true;
    this.pongTimeout = setTimeout(this.handlePongTimeout, this.config.pongTimeoutMs);
  };

  private handlePongTimeout = (): void => {
    this.clearPongTimeout();
    this.awaitingPong = false;
    this.config.onMissedHeartbeat();
  };

  private clearPongTimeout = (): void => {
    clearTimeout(this.pongTimeout);
    this.pongTimeout = undefined;
  };
}
